import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationsService } from "@/services/notificationsService";
import type { NotificationFilterParams } from "@/types/notification.dto";
import type { NotificationScope } from "@/components/shared/notifications/NotificationBell";
import { queryKeys } from "./keys";

export function useNotifications(
  scope: NotificationScope,
  filter: NotificationFilterParams = { pageSize: 20 },
) {
  return useQuery({
    queryKey:
      scope === "admin" ? queryKeys.notifications.admin(filter) : queryKeys.notifications.me(filter),
    queryFn: () =>
      scope === "admin"
        ? notificationsService.getAdminInbox(filter)
        : notificationsService.getMyNotifications(filter),
  });
}

export function useAdminNotifications(filter: NotificationFilterParams = { pageSize: 20 }) {
  return useNotifications("admin", filter);
}

export function useMyNotifications(filter: NotificationFilterParams = { pageSize: 20 }) {
  return useNotifications("me", filter);
}

export function useUnreadNotificationCount(scope: NotificationScope) {
  return useQuery({
    queryKey: queryKeys.notifications.unreadCount(scope),
    queryFn: async () => {
      const filter: NotificationFilterParams = { isRead: false, page: 1, pageSize: 1 };
      const result =
        scope === "admin"
          ? await notificationsService.getAdminInbox(filter)
          : await notificationsService.getMyNotifications(filter);
      return result.totalItems;
    },
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationReadMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => notificationsService.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useMarkAllNotificationsReadMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationsService.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}
